import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, UserCircle } from "lucide-react";
import { navigationLinks, siteConfig } from "../mock";
import { useAuth } from "../hooks/use-auth";
import { openExternal } from "../lib/external";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isAuthenticated, athlete, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-brand-700/95 backdrop-blur-md shadow-lg"
          : "bg-brand-700/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={siteConfig.logoTransparent}
            alt="CATV"
            className="h-12 w-auto object-contain group-hover:scale-105 transition-transform"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-bold text-white text-base tracking-tight">CATV</span>
            <span className="text-[10px] text-white/70 uppercase tracking-widest">
              Atletismo · Torres Vedras
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navigationLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive ? "text-white" : "text-white/75 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{link.label}</span>
                  {isActive && (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full bg-gradient-to-r from-ember-400 to-ember-600" />
                  )}
                </>
              )}
            </NavLink>
          ))}
          {isAuthenticated ? (
            <div className="ml-3 flex items-center gap-2">
              <button
                onClick={() => navigate("/area-socio/dashboard")}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium hover:bg-white/20 transition-colors cursor-pointer"
              >
                <UserCircle className="w-4 h-4" />
                {athlete?.name?.split(" ")[0] || "Atleta"}
              </button>
              <button
                onClick={() => { logout(); }}
                className="text-white/60 hover:text-white text-xs transition-colors"
              >
                Sair
              </button>
            </div>
          ) : (
            <Link
              to="/area-socio/login"
              className="ml-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-ember-400 to-ember-600 text-white text-sm font-semibold hover:from-ember-500 hover:to-ember-700 transition-colors shadow-md hover:shadow-lg"
            >
              <UserCircle className="w-4 h-4" />
              Área do Atleta
            </Link>
          )}
        </nav>

        <button
          className="md:hidden p-2 text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Abrir menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-brand-700">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navigationLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm font-medium ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/80 hover:bg-white/5"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            {isAuthenticated ? (
              <div className="mt-2 flex flex-col gap-1">
                <button
                  onClick={() => { setOpen(false); navigate("/area-socio/dashboard"); }}
                  className="flex items-center gap-2 px-3 py-3 rounded-md text-sm font-medium text-white bg-white/10"
                >
                  <UserCircle className="w-4 h-4" />
                  {athlete?.name || "Área do Atleta"}
                </button>
                <button
                  onClick={() => { setOpen(false); logout(); }}
                  className="px-3 py-2 rounded-md text-xs text-white/60 hover:text-white"
                >
                  Terminar sessão
                </button>
              </div>
            ) : (
              <Link
                to="/area-socio/login"
                className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-ember-400 to-ember-600 text-white text-sm font-semibold"
              >
                <UserCircle className="w-4 h-4" />
                Área do Atleta
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
